import React, { useState, useEffect, useMemo, useCallback } from 'react';
import styled from 'styled-components';
import { useNavigate, useParams, Link, useSearchParams } from 'react-router-dom';
import { theme } from '../../styles/theme';
import api from '../../config/api';
import toast from 'react-hot-toast';
import {
  MagnifyingGlassIcon,
  SignalIcon,
  PlusIcon,
  ArrowLeftIcon,
  ChevronRightIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline';
import {
  EXPLORER_HIDDEN_CATEGORIES,
  formatExplorerDisplayString,
  formatExplorerReading,
  groupCatalogForExplorer,
  catalogPathSuffix,
  statusColor,
  statusLabel,
  suggestWidgetName,
  suggestWidgetType,
} from '../../utils/signalkPathUtils';
import { writeSignalkPickerReturn } from '../../utils/signalkPickerReturn';

const Page = styled.div`
  min-height: 100vh;
  background: ${theme.colors.background.primary};
  padding: 1.5rem;
`;

const Header = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1rem;
  background: ${theme.colors.background.paper};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.lg};
  padding: 1rem 1.25rem;
`;

const Title = styled.h1`
  margin: 0;
  font-size: ${theme.fontSizes.xl};
  color: ${theme.colors.text.primary};
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const Toolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  align-items: center;
`;

const Select = styled.select`
  padding: 0.5rem 0.75rem;
  border-radius: ${theme.borderRadius.md};
  border: 1px solid ${theme.colors.border.secondary};
  background: ${theme.colors.background.secondary};
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.sm};
`;

const SearchWrap = styled.div`
  position: relative;
  min-width: 220px;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 0.5rem 0.75rem 0.5rem 2rem;
  border-radius: ${theme.borderRadius.md};
  border: 1px solid ${theme.colors.border.secondary};
  background: ${theme.colors.background.secondary};
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.sm};
`;

const SearchIcon = styled(MagnifyingGlassIcon)`
  position: absolute;
  left: 0.5rem;
  top: 50%;
  transform: translateY(-50%);
  width: 1rem;
  height: 1rem;
  color: ${theme.colors.text.secondary};
`;

const Panel = styled.div`
  background: ${theme.colors.background.paper};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.lg};
  overflow: hidden;
  display: flex;
  flex-direction: column;
  max-height: calc(100vh - 220px);
`;

const ListWrap = styled.div`
  overflow: auto;
  flex: 1;
`;

const CategoryBlock = styled.div`
  border-bottom: 1px solid ${theme.colors.border.primary};
`;

const CategoryHeader = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  background: ${theme.colors.background.secondary};
  border: none;
  cursor: pointer;
  text-align: left;
  color: ${theme.colors.text.primary};
  font-weight: ${theme.fontWeights.semibold};
  font-size: ${theme.fontSizes.sm};

  &:hover {
    background: ${theme.colors.background.primary};
  }
`;

const CollectionHeader = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.55rem 1rem 0.55rem 2rem;
  background: ${theme.colors.background.paper};
  border: none;
  border-top: 1px solid ${theme.colors.border.secondary};
  cursor: pointer;
  text-align: left;
  color: ${theme.colors.text.primary};
  font-weight: ${theme.fontWeights.medium};
  font-size: ${theme.fontSizes.sm};

  &:hover {
    background: ${theme.colors.background.secondary};
  }
`;

const PathRow = styled.div`
  display: grid;
  grid-template-columns: 36px minmax(140px, 1.2fr) minmax(120px, 1fr) 88px 100px;
  gap: 0.5rem;
  align-items: center;
  padding: 0.45rem 1rem 0.45rem 3.25rem;
  border-top: 1px solid ${theme.colors.border.secondary};
  font-size: ${theme.fontSizes.sm};
  background: ${theme.colors.background.primary};

  @media (max-width: 900px) {
    grid-template-columns: 36px 1fr 100px;
    .explorer-meta {
      display: none;
    }
  }
`;

const ReadingLabel = styled.div`
  font-weight: ${theme.fontWeights.medium};
  color: ${theme.colors.text.primary};
`;

const ReadingSub = styled.div`
  font-size: ${theme.fontSizes.xs};
  color: ${theme.colors.text.secondary};
  font-family: ${theme.typography.fontFamily.mono};
  margin-top: 0.1rem;
`;

const ValueCell = styled.div`
  font-weight: ${theme.fontWeights.semibold};
  color: ${theme.colors.text.primary};
  font-variant-numeric: tabular-nums;
`;

const Checkbox = styled.input`
  cursor: pointer;
`;

const StatusDot = styled.span`
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-right: 0.35rem;
  background: ${(p) => p.$color};
`;

const CountBadge = styled.span`
  margin-left: auto;
  font-size: ${theme.fontSizes.xs};
  color: ${theme.colors.text.secondary};
  font-weight: ${theme.fontWeights.normal};
`;

const Footer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 1rem;
  border-top: 1px solid ${theme.colors.border.primary};
  background: ${theme.colors.background.secondary};
  font-size: ${theme.fontSizes.sm};
  color: ${theme.colors.text.secondary};
`;

const Button = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.5rem 1rem;
  border-radius: ${theme.borderRadius.md};
  cursor: pointer;
  font-weight: ${theme.fontWeights.medium};
  font-size: ${theme.fontSizes.sm};
  background: ${(p) => (p.$primary ? theme.colors.primary.main : theme.colors.background.paper)};
  color: ${(p) => (p.$primary ? theme.colors.primary.contrast : theme.colors.text.primary)};
  border: 1px solid
    ${(p) => (p.$primary ? theme.colors.primary.main : theme.colors.border.secondary)};

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ColumnHeader = styled.div`
  display: grid;
  grid-template-columns: 36px minmax(140px, 1.2fr) minmax(120px, 1fr) 88px 100px;
  gap: 0.5rem;
  padding: 0.5rem 1rem 0.5rem 1rem;
  font-size: ${theme.fontSizes.xs};
  font-weight: ${theme.fontWeights.medium};
  color: ${theme.colors.text.secondary};
  background: ${theme.colors.background.secondary};
  border-bottom: 1px solid ${theme.colors.border.primary};
  position: sticky;
  top: 0;
  z-index: 2;

  @media (max-width: 900px) {
    grid-template-columns: 36px 1fr 100px;
    .explorer-meta {
      display: none;
    }
  }
`;

function formatTs(ts) {
  if (!ts) return '—';
  try {
    return new Date(ts).toLocaleTimeString();
  } catch {
    return String(ts);
  }
}

function selectionState(paths, selected) {
  const keys = paths.map((p) => p.path);
  const selectedCount = keys.filter((k) => selected.has(k)).length;
  return {
    all: keys.length > 0 && selectedCount === keys.length,
    some: selectedCount > 0 && selectedCount < keys.length,
    count: selectedCount,
  };
}

const SignalKExplorer = () => {
  const { vesselId: vesselIdParam } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const vesselId = parseInt(vesselIdParam, 10);
  const queryDashboardId = searchParams.get('dashboardId') || '';
  const querySectionId = searchParams.get('sectionId') || '';
  const queryWidgetId = searchParams.get('widgetId') || '';
  const queryPicker = searchParams.get('picker') === '1';
  const queryReturnTo = searchParams.get('returnTo') || '';
  const isWidgetPicker = Boolean(queryDashboardId && queryWidgetId && queryPicker);

  const [vessel, setVessel] = useState(null);
  const [paths, setPaths] = useState([]);
  const [counts, setCounts] = useState({ live: 0, stale: 0, archived: 0, total: 0 });
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState(() => searchParams.get('category') || '');
  const [search, setSearch] = useState('');
  const [hideNoise, setHideNoise] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [dashboards, setDashboards] = useState([]);
  const [targetDashboardId, setTargetDashboardId] = useState(queryDashboardId);
  const [targetSectionId] = useState(querySectionId);
  const [adding, setAdding] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState(() => new Set(['electrical']));
  const [expandedCollections, setExpandedCollections] = useState(() => new Set());
  const [electricalExpanded, setElectricalExpanded] = useState(false);

  const loadCatalog = useCallback(async () => {
    if (!vesselId) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ status: statusFilter, limit: '10000' });
      if (categoryFilter) params.set('category', categoryFilter);
      if (search.trim()) params.set('search', search.trim());

      const [catalogRes, vesselRes, dashRes] = await Promise.all([
        api.get(`/api/vessels/${vesselId}/signalk/catalog?${params}`),
        api.get(`/api/vessels/${vesselId}`),
        api.get('/api/dashboards'),
      ]);

      setPaths(catalogRes.data.paths || []);
      setCounts(catalogRes.data.counts || { live: 0, stale: 0, archived: 0, total: 0 });
      setVessel(vesselRes.data);

      const vesselDashboards = (dashRes.data || []).filter(
        (d) => Number(d.vessel_id) === vesselId
      );
      setDashboards(vesselDashboards);
      if (queryDashboardId && vesselDashboards.some((d) => String(d.id) === String(queryDashboardId))) {
        setTargetDashboardId(String(queryDashboardId));
      } else if (!targetDashboardId && vesselDashboards.length) {
        setTargetDashboardId(String(vesselDashboards[0].id));
      }
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.error || 'Failed to load SignalK catalog');
      setPaths([]);
    } finally {
      setLoading(false);
    }
  }, [vesselId, statusFilter, categoryFilter, search, queryDashboardId]);

  useEffect(() => {
    if (queryDashboardId) setTargetDashboardId(String(queryDashboardId));
  }, [queryDashboardId]);

  useEffect(() => {
    loadCatalog();
    const interval = setInterval(loadCatalog, 30000);
    return () => clearInterval(interval);
  }, [loadCatalog]);

  const categories = useMemo(() => {
    const set = new Set(paths.map((p) => p.category).filter(Boolean));
    return [...set].sort();
  }, [paths]);

  const visiblePaths = useMemo(() => {
    return paths.filter((p) => !hideNoise || !EXPLORER_HIDDEN_CATEGORIES.has(p.category));
  }, [paths, hideNoise]);

  const grouped = useMemo(() => groupCatalogForExplorer(visiblePaths), [visiblePaths]);

  const isSearching = Boolean(search.trim());

  useEffect(() => {
    if (electricalExpanded || isSearching || grouped.length === 0) return;
    const electrical = grouped.find((g) => g.category === 'electrical');
    if (!electrical) return;
    setExpandedCategories((prev) => new Set([...prev, 'electrical']));
    setExpandedCollections((prev) => {
      const next = new Set(prev);
      electrical.collections.forEach((c) => next.add(c.key));
      return next;
    });
    setElectricalExpanded(true);
  }, [grouped, isSearching, electricalExpanded]);

  useEffect(() => {
    if (!isSearching || grouped.length === 0) return;
    setExpandedCategories(new Set(grouped.map((g) => g.category)));
    const collKeys = grouped.flatMap((g) => g.collections.map((c) => c.key));
    setExpandedCollections(new Set(collKeys));
  }, [isSearching, grouped]);

  const toggleCategory = (category) => {
    setExpandedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(category)) next.delete(category);
      else next.add(category);
      return next;
    });
  };

  const toggleCollection = (key) => {
    setExpandedCollections((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const expandAll = () => {
    setExpandedCategories(new Set(grouped.map((g) => g.category)));
    setExpandedCollections(
      new Set(grouped.flatMap((g) => g.collections.map((c) => c.key)))
    );
  };

  const collapseAll = () => {
    setExpandedCategories(new Set());
    setExpandedCollections(new Set());
  };

  const togglePaths = (pathRows, checked) => {
    setSelected((prev) => {
      const next = new Set(prev);
      for (const row of pathRows) {
        if (checked) next.add(row.path);
        else next.delete(row.path);
      }
      return next;
    });
  };

  const togglePath = (path) => {
    setSelected((prev) => {
      if (isWidgetPicker) {
        return new Set([path]);
      }
      const next = new Set(prev);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      return next;
    });
  };

  const applyPathToWidget = (path) => {
    if (!path) {
      toast.error('Select a path first');
      return;
    }
    if (!queryDashboardId) {
      toast.error('Missing dashboard context');
      return;
    }
    writeSignalkPickerReturn({
      dashboardId: queryDashboardId,
      widgetId: queryWidgetId,
      path,
      sectionId: querySectionId,
    });
    toast.success('Returning to widget editor…');
    const returnPath =
      queryReturnTo ||
      (queryDashboardId ? `/dashboard/${queryDashboardId}/edit` : '/dashboards');
    navigate(returnPath);
  };

  const handleUsePathForWidget = () => {
    const pathsToAdd = [...selected];
    if (!pathsToAdd.length) {
      toast.error('Select a path');
      return;
    }
    if (pathsToAdd.length > 1) {
      toast.error('Select only one path when editing a widget');
      return;
    }
    applyPathToWidget(pathsToAdd[0]);
  };

  const allVisibleSelected =
    visiblePaths.length > 0 && visiblePaths.every((p) => selected.has(p.path));

  const toggleAll = () => {
    togglePaths(visiblePaths, !allVisibleSelected);
  };

  const handleAddToDashboard = async () => {
    if (!targetDashboardId) {
      toast.error('Select a dashboard first');
      return;
    }
    const pathsToAdd = [...selected];
    if (!pathsToAdd.length) {
      toast.error('Select at least one path');
      return;
    }

    setAdding(true);
    try {
      const widgets = pathsToAdd.map((path) => ({
        data_path: path,
        widget_name: suggestWidgetName(path),
        widget_type: suggestWidgetType(path),
        widget_category: path.split('.')[0],
      }));

      const res = await api.post(`/api/dashboards/${targetDashboardId}/widgets/batch`, {
        widgets,
        section_id: targetSectionId ? parseInt(targetSectionId, 10) : undefined,
      });

      if (!res.data?.created) {
        toast.error('No widgets were created — check path selection');
        return;
      }

      toast.success(`Added ${res.data.created} widget(s) to dashboard`);
      setSelected(new Set());
      const sectionQuery = targetSectionId ? `?sectionId=${targetSectionId}` : '';
      navigate(`/dashboard/${targetDashboardId}/edit${sectionQuery}`);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to add widgets');
    } finally {
      setAdding(false);
    }
  };

  if (!vesselId) {
    return (
      <Page>
        <p>Invalid vessel.</p>
        <Link to="/vessels">Back to vessels</Link>
      </Page>
    );
  }

  return (
    <Page>
      <Header>
        <div>
          <Button type="button" onClick={() => navigate(-1)}>
            <ArrowLeftIcon className="w-4 h-4" />
            Back
          </Button>
          <Title style={{ marginTop: '0.75rem' }}>
            <SignalIcon className="w-6 h-6" />
            {isWidgetPicker ? 'Pick SignalK path for widget' : 'SignalK Explorer'}
            {vessel?.vessel_name ? ` — ${vessel.vessel_name}` : ''}
          </Title>
          <div style={{ fontSize: theme.fontSizes.sm, color: theme.colors.text.secondary, marginTop: '0.25rem' }}>
            {isWidgetPicker
              ? 'Select one path — it will apply to the widget you are editing (not create a new widget).'
              : 'Paths grouped by system — expand a section to browse and select.'}
          </div>
        </div>
        <Toolbar>
          <SearchWrap>
            <SearchIcon />
            <SearchInput
              placeholder="Search paths…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && loadCatalog()}
            />
          </SearchWrap>
          <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="all">All statuses</option>
            <option value="live">Live only</option>
            <option value="stale">Stale</option>
            <option value="archived">Archived</option>
          </Select>
          <Select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}>
            <option value="">All categories</option>
            {categories.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </Select>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', fontSize: theme.fontSizes.sm }}>
            <Checkbox
              type="checkbox"
              checked={hideNoise}
              onChange={(e) => setHideNoise(e.target.checked)}
            />
            Hide host/provider
          </label>
          <Button type="button" onClick={expandAll}>
            Expand all
          </Button>
          <Button type="button" onClick={collapseAll}>
            Collapse all
          </Button>
          <Link
            to={`/vessels/${vesselId}/live`}
            style={{ fontSize: theme.fontSizes.sm, color: theme.colors.primary.main, alignSelf: 'center' }}
          >
            Live Boat →
          </Link>
          <Button type="button" onClick={loadCatalog}>
            Refresh
          </Button>
        </Toolbar>
      </Header>

      <Panel>
        <ColumnHeader>
          <Checkbox type="checkbox" checked={allVisibleSelected} onChange={toggleAll} />
          <span>Reading</span>
          <span>Value</span>
          <span className="explorer-meta">Status</span>
          <span className="explorer-meta">Updated</span>
        </ColumnHeader>

        <ListWrap>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '2rem', color: theme.colors.text.secondary }}>
              Loading catalog…
            </div>
          ) : grouped.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '2rem', color: theme.colors.text.secondary }}>
              No paths match your filters. Data appears here as the boat streams SignalK.
            </div>
          ) : (
            grouped.map((cat) => {
              const catExpanded = expandedCategories.has(cat.category);
              const catPaths = cat.collections.flatMap((c) => c.paths);
              const catSel = selectionState(catPaths, selected);

              return (
                <CategoryBlock key={cat.category}>
                  <CategoryHeader type="button" onClick={() => toggleCategory(cat.category)}>
                    {catExpanded ? (
                      <ChevronDownIcon className="w-4 h-4" />
                    ) : (
                      <ChevronRightIcon className="w-4 h-4" />
                    )}
                    <Checkbox
                      type="checkbox"
                      checked={catSel.all}
                      ref={(el) => {
                        if (el) el.indeterminate = catSel.some;
                      }}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) => togglePaths(catPaths, e.target.checked)}
                    />
                    <span>{cat.label}</span>
                    <CountBadge>
                      {cat.pathCount} paths · {cat.collections.length} groups
                      {catSel.count > 0 ? ` · ${catSel.count} selected` : ''}
                    </CountBadge>
                  </CategoryHeader>

                  {catExpanded &&
                    cat.collections.map((coll) => {
                      const collExpanded = expandedCollections.has(coll.key);
                      const collSel = selectionState(coll.paths, selected);

                      return (
                        <div key={coll.key}>
                          <CollectionHeader
                            type="button"
                            onClick={() => toggleCollection(coll.key)}
                          >
                            {collExpanded ? (
                              <ChevronDownIcon className="w-4 h-4" />
                            ) : (
                              <ChevronRightIcon className="w-4 h-4" />
                            )}
                            <Checkbox
                              type="checkbox"
                              checked={collSel.all}
                              ref={(el) => {
                                if (el) el.indeterminate = collSel.some;
                              }}
                              onClick={(e) => e.stopPropagation()}
                              onChange={(e) => togglePaths(coll.paths, e.target.checked)}
                            />
                            <span>{coll.label}</span>
                            <CountBadge>
                              {coll.paths.length} paths
                              {collSel.count > 0 ? ` · ${collSel.count} selected` : ''}
                            </CountBadge>
                          </CollectionHeader>

                          {collExpanded &&
                            coll.paths.map((row) => {
                              const suffix = catalogPathSuffix(row.path, coll.key);
                              const reading = formatExplorerReading(row.path, row.value, row.units);
                              const valueText = formatExplorerDisplayString(
                                row.path,
                                row.value,
                                row.units
                              );

                              return (
                              <PathRow key={row.path}>
                                <Checkbox
                                  type="checkbox"
                                  checked={selected.has(row.path)}
                                  onChange={() => togglePath(row.path)}
                                />
                                <div title={row.path}>
                                  <ReadingLabel>{reading.label}</ReadingLabel>
                                  <ReadingSub>{suffix}</ReadingSub>
                                </div>
                                <ValueCell>{valueText}</ValueCell>
                                <span className="explorer-meta">
                                  <StatusDot $color={statusColor(row.status, theme)} />
                                  {statusLabel(row.status)}
                                </span>
                                <span className="explorer-meta">{formatTs(row.lastSeen)}</span>
                                {isWidgetPicker ? (
                                  <Button
                                    type="button"
                                    $primary
                                    style={{ padding: '0.25rem 0.5rem', fontSize: theme.fontSizes.xs }}
                                    onClick={() => applyPathToWidget(row.path)}
                                  >
                                    Use path
                                  </Button>
                                ) : null}
                              </PathRow>
                              );
                            })}
                        </div>
                      );
                    })}
                </CategoryBlock>
              );
            })
          )}
        </ListWrap>

        <Footer>
          <div>
            {counts.total} total ({counts.live} live, {counts.stale} stale, {counts.archived} archived)
            {' · '}
            {grouped.length} categories · {visiblePaths.length} paths · Selected {selected.size}
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
            {isWidgetPicker ? (
              <>
                <Button type="button" onClick={() => navigate(queryReturnTo || `/dashboard/${queryDashboardId}/edit`)}>
                  Cancel
                </Button>
                <Button
                  type="button"
                  $primary
                  disabled={selected.size === 0}
                  onClick={handleUsePathForWidget}
                >
                  Apply path to widget
                </Button>
              </>
            ) : (
              <>
                <Select
                  value={targetDashboardId}
                  onChange={(e) => setTargetDashboardId(e.target.value)}
                  style={{ minWidth: 180 }}
                >
                  <option value="">Choose dashboard…</option>
                  {dashboards.map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.dashboard_name || d.name}
                    </option>
                  ))}
                </Select>
                <Button
                  type="button"
                  $primary
                  disabled={adding || selected.size === 0 || !targetDashboardId}
                  onClick={handleAddToDashboard}
                >
                  <PlusIcon className="w-4 h-4" />
                  {adding ? 'Adding…' : `Add ${selected.size || ''} to dashboard`}
                </Button>
              </>
            )}
          </div>
        </Footer>
      </Panel>
    </Page>
  );
};

export default SignalKExplorer;
